import { ReportPeriod, ReportType } from "@/components/common/report-chip";
import { cn } from "@/lib/utils";
import { AlertTriangle, Equal } from "lucide-react";
import type { ReactNode } from "react";
import { FileSummaryCard } from "./file-summary-card";
import { ParsedDsdMeta } from "./parse-dsd-filename";

/** 업로드 방식 — 최신 버전 추가 vs 별도 보고서 */
export type UploadMode = "version" | "separate";

export interface ExistingReportSummary {
  /** 최신 버전으로 이어붙일 대상 보고서 id (버전 포워드 srcWorkbookId) */
  id: string;
  name: string;
  type?: ReportType;
  year?: number;
  period?: ReportPeriod;
  uploadDate: string;
  /** 목록에서 대상 보고서를 아직 못 찾았으면 undefined — 그 줄은 노출하지 않는다. */
  currentVersion?: string;
}

const RadioRow = ({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex min-w-0 flex-1 items-center gap-2 rounded-lg border py-4 pl-4 pr-3 text-left transition-colors",
      selected
        ? "border-blue-500 bg-blue-50"
        : "border-line-normal hover:bg-neutral-50",
    )}
  >
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-full border-2",
        selected ? "border-blue-500" : "border-neutral-300",
      )}
    >
      {selected && <span className="size-2.5 rounded-full bg-blue-500" />}
    </span>
    <span className="truncate body-2-normal-medium text-label-normal">
      {children}
    </span>
  </button>
);

// ============================================
// SamePeriodSection
// 동일 기간 보고서가 이미 있을 때: 경고 배너 + (업로드 영역 = children) + 업로드 방식 선택.
// ============================================

export const SamePeriodSection = ({
  mode,
  onModeChange,
  children,
}: {
  mode: UploadMode;
  onModeChange: (mode: UploadMode) => void;
  children: ReactNode;
}) => (
  <div className="flex w-full flex-1 flex-col gap-2">
    <div className="flex items-start gap-1 rounded-lg border-2 border-amber-100 bg-amber-100 p-2 label-2-regular text-neutral-800">
      <AlertTriangle className="size-[18px] shrink-0 text-amber-500" />
      <span className="flex-1">
        동일한 기간에 이미 등록된 보고서가 있습니다. 최신 버전으로
        추가하시겠습니까?
      </span>
    </div>

    {children}

    <div className="flex gap-2">
      <RadioRow
        selected={mode === "version"}
        onClick={() => onModeChange("version")}
      >
        최신 버전으로 업로드
      </RadioRow>
      <RadioRow
        selected={mode === "separate"}
        onClick={() => onModeChange("separate")}
      >
        별도 보고서로 업로드
      </RadioRow>
    </div>
  </div>
);

// ============================================
// SamePeriodComparison
// 업로드 영역 안, 신규 파일(왼쪽) ↔ 기존 보고서(오른쪽) 비교. 제거 버튼은 신규 카드 우상단 모서리에 걸친다.
// items-stretch 로 두 카드 높이를 맞춘다 — 내용 길이가 달라도 같은 크기로 보여야 한다.
// ============================================

export const SamePeriodComparison = ({
  newFilename,
  newMeta,
  existing,
  onRemove,
}: {
  newFilename: string;
  newMeta: ParsedDsdMeta;
  existing: ExistingReportSummary;
  onRemove: () => void;
}) => (
  <div className="flex items-stretch gap-1 rounded-lg bg-background-gray-alternative p-3">
    <div className="relative flex min-w-0 flex-1">
      <FileSummaryCard
        className="flex-1"
        meta={newMeta}
        title={newFilename}
        isNew
      />
      <button
        type="button"
        aria-label="파일 제거"
        className="absolute -right-3 -top-3 rounded-full"
        onClick={onRemove}
      >
        <img alt="" src="/icons/cancel-rounded.svg" className="size-6" />
      </button>
    </div>
    <Equal className="size-[18px] shrink-0 self-center text-label-alternative" />
    <FileSummaryCard
      className="min-w-0 flex-1"
      meta={{
        type: existing.type,
        year: existing.year,
        period: existing.period,
      }}
      title={existing.name}
      info={[
        ...(existing.currentVersion
          ? [{ label: "현재 버전", value: existing.currentVersion }]
          : []),
        { label: "업로드일", value: existing.uploadDate },
      ]}
    />
  </div>
);
